import {
  ApacCandidateHeader,
  ApacCandidateFooter,
} from '@seek/apac-candidate-header-footer';
import type { SiteName } from '@seek/melways-sites';
import { id_jobstreet_com } from '@seek/online-identity';
import { Box, PageBlock, ToastProvider } from 'braid-design-system';
import type { ReactNode } from 'react';

import { Banner } from 'src/components/atoms/Banner/Banner';
import GroupFooter from 'src/components/atoms/GroupFooter/GroupFooter';
import LoginModal from 'src/components/atoms/LoginModal/LoginModal';
import { NAV_PATHS } from 'src/paths';
import { useAuthStatus } from 'src/provider/auth';
import { useConfig } from 'src/provider/config';
import { LoginModalProvider } from 'src/provider/loginModal';

interface Props {
  children: ReactNode;
}

const getLanguageSwitcher = (siteName: SiteName, urlWithoutLang: string) =>
  siteName === id_jobstreet_com.siteName
    ? {
        en: {
          url: `${urlWithoutLang}`,
        },
        id: {
          url: `/id${urlWithoutLang}`,
        },
      }
    : {};

export function Layout({ children }: Props) {
  const { site, absoluteUrl } = useConfig();
  const { authStatus, userProfile } = useAuthStatus();
  const { pathname: currentUrl } = new URL(absoluteUrl);

  const urlWithoutLang = currentUrl.replace(/^\/(en|id)/, '');
  const languageSwitcher: Partial<Record<string, { url: string }>> =
    getLanguageSwitcher(site, urlWithoutLang);
  const handleLanguageSwitch = (lang: string) => {
    if (languageSwitcher[lang]) {
      window.location.href = languageSwitcher[lang].url;
    }
  };

  return (
    <>
      <ToastProvider>
        <LoginModalProvider>
          <ApacCandidateHeader
            authenticationStatus={authStatus}
            userProfile={userProfile}
            languageSwitcher={languageSwitcher}
            onLanguageSwitch={handleLanguageSwitch}
            returnUrlForPostAuth={NAV_PATHS.communityHome}
            leanHeader={false}
            widthMode="large"
            activePrimaryTab="community"
          />
          <Banner />
          <PageBlock width="large">
            <Box>{children}</Box>
            <LoginModal />
          </PageBlock>
          <GroupFooter />
          <ApacCandidateFooter
            authenticationStatus={authStatus}
            isExpandedVersion={true}
          />
        </LoginModalProvider>
      </ToastProvider>
    </>
  );
}
